import React from 'react';

const About = () => {
  return (
    <section id="about" className="py-24 bg-[#050505] relative overflow-hidden" data-testid="about-section">
      {/* Subtle red glow */}
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-red-900/10 rounded-full blur-3xl -translate-y-1/2" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-heading tracking-tight uppercase" data-testid="about-heading">
            <span className="text-white">About </span>
            <span className="text-gradient-noir">UVD</span>
          </h2>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Image */}
          <div className="lg:col-span-2 flex justify-center">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3379932/pexels-photo-3379932.jpeg?auto=compress&cs=tinysrgb&w=600"
                alt="Cinematographer on set"
                className="w-full max-w-sm object-cover grayscale contrast-125 brightness-75"
                style={{ aspectRatio: '3/4' }}
                data-testid="about-image"
              />
              {/* Red accent line */}
              <div className="absolute -bottom-3 -right-3 w-full h-full border border-red-700/30 -z-10" />
            </div>
          </div>

          {/* Bio Text */}
          <div className="lg:col-span-3 space-y-6" data-testid="about-content">
            <p className="text-xl md:text-2xl font-semibold text-white leading-snug">
              UVD Media was founded by <span className="text-red-500">Coach Dooley</span>, a working author and visual storyteller who lives at the intersection of story and screen.
            </p>

            <p className="text-base text-gray-400 leading-relaxed">
              After years of coaching, writing and publishing his own books, he began producing cinematic trailers and promo videos to bring those stories to life online - and quickly saw how powerful the right visuals could be for discovery and sales.
            </p>

            <p className="text-base text-gray-400 leading-relaxed">
              Drawing on his background in video production and commercial work, Coach Dooley built UVD Media as a lean, modern studio focused on one thing: <span className="text-white font-semibold">turning books into compelling, launch-ready video trailers</span>.
            </p>

            <p className="text-base text-gray-400 leading-relaxed">
              He works end-to-end - from concept, scripting, and story beats to editing, motion graphics, and platform-specific cuts for TikTok, Instagram Reels, YouTube, and beyond - often stitching together AI-native tools (like generative video, VO, and design) with traditional editing in <span className="text-red-500 font-semibold">DaVinci Resolve</span>.
            </p>

            {/* Quote */}
            <div className="border-l-2 border-red-700 pl-6 my-8">
              <p className="text-lg text-gray-300 italic">
                "UVD focuses on freelance book authors and small businesses because that's where Coach Dooley comes from. He knows what it's like to have a strong story but limited time, budget, and reach."
              </p>
            </div>

            <p className="text-base text-gray-400 leading-relaxed">
              UVD exists to give indie creators and small brands the same kind of polished, strategic video presence usually reserved for bigger budgets - so they can <span className="text-white font-semibold">show up like a serious player, even if they're launching from their living room</span>.
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {[
            { stat: 'Author-Led', desc: 'Founded by a working author who understands your world' },
            { stat: 'End-to-End', desc: 'From concept to delivery, we handle everything' },
            { stat: 'Modern Tools', desc: 'AI-native + traditional editing for best results' }
          ].map((item, i) => (
            <div key={i} className="bg-[#0A0A0A] border border-white/10 hover:border-red-700/30 p-6 text-center transition-all duration-300" data-testid={`about-stat-${i}`}>
              <div className="text-2xl font-heading uppercase tracking-wide text-gradient-noir mb-2">{item.stat}</div>
              <p className="text-sm text-gray-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
